import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

export type LoadTotals = { cases: number; kg: number; m3: number };

const ZERO: LoadTotals = { cases: 0, kg: 0, m3: 0 };

/**
 * 勤務ごとの積載ピークの合計。
 *
 * 旧 /plans（PlanningTrucks.presenter の totalMaxLoad*）と同じ定義で、勤務ごとに operations を
 * 到着順に見て「積」で加算・「降」で減算し、その途中の最大値を採って勤務間で足す。
 * 総荷物量（積込の合計）を分子にすると、回転がある日は同時に載らない量まで数えてしまう。
 *
 * 「降」は降ろす前の量もピークの候補にする（旧実装が currentWeightG に減算前の値を入れているのと同じ）。
 */
export function sumPeakLoads(deliveries: readonly PlanningsDeliveryWithLegRunEntity[]): LoadTotals {
  return deliveries.reduce<LoadTotals>((total, delivery) => {
    const peak = peakOf(delivery);
    return { cases: total.cases + peak.cases, kg: total.kg + peak.kg, m3: total.m3 + peak.m3 };
  }, ZERO);
}

function peakOf(delivery: PlanningsDeliveryWithLegRunEntity): LoadTotals {
  const legRunById = new Map(delivery.shipmentOrderLegRuns.map((lr) => [lr.id, lr]));
  // deliveries API は到着順で返す契約だが、並びが崩れても同じ答えになるようここで並べ直す
  const operations = [...delivery.operations].sort((a, b) => a.arrivalAt.localeCompare(b.arrivalAt));

  let current = ZERO;
  let peak = ZERO;
  for (const operation of operations) {
    const legRun = legRunById.get(operation.shipmentOrderLegRunId);
    if (!legRun) continue;

    const load: LoadTotals = {
      cases: legRun.item_count ?? 0,
      kg: legRun.item_total_weight_kg ?? 0,
      m3: legRun.item_total_volume_m3 ?? 0,
    };
    if (operation.action === '積') {
      current = { cases: current.cases + load.cases, kg: current.kg + load.kg, m3: current.m3 + load.m3 };
      peak = maxOf(peak, current);
    } else {
      peak = maxOf(peak, current);
      current = { cases: current.cases - load.cases, kg: current.kg - load.kg, m3: current.m3 - load.m3 };
    }
  }
  return peak;
}

const maxOf = (a: LoadTotals, b: LoadTotals): LoadTotals => ({
  cases: Math.max(a.cases, b.cases),
  kg: Math.max(a.kg, b.kg),
  m3: Math.max(a.m3, b.m3),
});

/** 積載率 (%)。分母が無い・0 の軸は null（画面は「積載率 -%」を出す） */
export function loadingRatePercent(load: number, capacity: number | null | undefined): number | null {
  if (capacity == null || capacity <= 0) return null;
  return Math.round((load / capacity) * 100);
}

/** 積載率の表示。分母が無い軸は旧 /plans と同じく「-」を出す（行ごと消すと未設定に気づけない） */
export function ratePercentText(rate: number | null): string {
  return rate == null ? '-' : String(rate);
}

/**
 * 数量の積載率の分母。配車がある勤務の最大積載数量を合算する。
 *
 * 旧 /plans と同じく勤務ごとに足す（同じトラックに 2 勤務あれば 2 台分数える）。未設定は
 * 除いて合算する。1 台でも未設定だと全体を捨てる作りだったため、率が出ない日があった。
 */
export function sumCapacityQuantity(
  deliveries: readonly PlanningsDeliveryWithLegRunEntity[],
): number | null {
  const values = deliveries
    .filter((d) => d.operations.length > 0)
    .map((d) => d.maximumLoadingCapacityQuantity)
    .filter((v): v is number => v != null && v > 0);
  if (values.length === 0) return null;
  return values.reduce((prev, current) => prev + current, 0);
}

/**
 * 案件（leg_run）の請求・支払金額の合計。
 *
 * 集計元は shift_truck_tariff_results ではなく案件の金額（旧 /plans と同じ）。伝票詳細で
 * 入力した金額も運賃計算の結果も、どちらもここに入る。同じ案件が複数の勤務・回転に出ることが
 * あるので leg_run 単位で重複を落とす。
 *
 * hasAmount は「金額が 1 件でも登録されているか」。0 円と未登録を呼び出し側で見分けるために
 * 返す（車両ごとの粗利は未登録なら出さない。0 円と出すと入力済みに見える）。
 */
export function sumLegRunAmounts(
  deliveries: readonly PlanningsDeliveryWithLegRunEntity[],
): { billingYen: number; paymentYen: number; hasAmount: boolean } {
  const billingByLegRunId = new Map<number, number>();
  const paymentByLegRunId = new Map<number, number>();

  deliveries.forEach((delivery) => {
    delivery.shipmentOrderLegRuns.forEach((legRun) => {
      const billing = legRun.shipment_order_billing_amount;
      if (billing && !billingByLegRunId.has(legRun.id)) {
        billingByLegRunId.set(legRun.id, billing.total_amount ?? 0);
      }
      const payment = legRun.shipment_order_leg_run_payment_amount;
      if (payment && !paymentByLegRunId.has(legRun.id)) {
        paymentByLegRunId.set(legRun.id, payment.total_amount ?? 0);
      }
    });
  });

  const sum = (m: Map<number, number>) => [...m.values()].reduce((prev, current) => prev + current, 0);
  return {
    billingYen: sum(billingByLegRunId),
    paymentYen: sum(paymentByLegRunId),
    hasAmount: billingByLegRunId.size > 0 || paymentByLegRunId.size > 0,
  };
}
